/**
 * Cars render pass — instanced oriented box, one per car maintained by src/cars.
 * Mirrors render/trees verbatim except for:
 *   - 16-byte instance struct = pos (vec3) + heading (f32)
 *   - cube geometry (36 verts) instead of cone+trunk
 *   - fixed instance count (no GPU atomic — CPU knows N), so we write it directly.
 */
import { createRenderPipeline } from '../gpu/pipelines.ts';
import { createEmptyBindGroup } from '../gpu/buffers.ts';
import cubeWgsl from './cube.wgsl?raw';
import shadowPcfWgsl from './shadow_pcf.wgsl?raw';
import fogWgsl from './fog.wgsl?raw';
import carsWgsl from './cars.wgsl?raw';

const wgsl = cubeWgsl + '\n' + carsWgsl + '\n' + shadowPcfWgsl + '\n' + fogWgsl;

export interface CarsPass {
  draw(pass: GPURenderPassEncoder): void;
  destroy(): void;
}

export interface CarsPassOptions {
  presentationFormat: GPUTextureFormat;
  depthFormat: GPUTextureFormat;
  instanceBuffer: GPUBuffer;
  /** Fixed instance count (`config.carInstanceCount`). */
  instanceCount: number;
  cameraBindGroupLayout: GPUBindGroupLayout;
  dayNightBindGroupLayout: GPUBindGroupLayout;
  shadowBindGroupLayout: GPUBindGroupLayout;
}

const INSTANCE_STRIDE = 16;
const CUBE_VERTEX_COUNT = 36;
const INDIRECT_DRAW_BYTES = 16;

export function createCarsPass(device: GPUDevice, opts: CarsPassOptions): CarsPass {
  const empty = createEmptyBindGroup(device, 'cars');

  const pipelineLayout = device.createPipelineLayout({
    label: 'cars.pll',
    bindGroupLayouts: [
      opts.cameraBindGroupLayout,
      empty.layout,
      opts.dayNightBindGroupLayout,
      opts.shadowBindGroupLayout,
    ],
  });

  const pipeline = createRenderPipeline(device, wgsl, {
    label: 'cars',
    layout: pipelineLayout,
    buffers: [{
      arrayStride: INSTANCE_STRIDE,
      stepMode: 'instance',
      attributes: [
        { shaderLocation: 0, offset: 0,  format: 'float32x3' }, // pos
        { shaderLocation: 1, offset: 12, format: 'float32' },   // heading (radians)
      ],
    }],
    targets: [{ format: opts.presentationFormat }],
    primitive: { topology: 'triangle-list', cullMode: 'back', frontFace: 'ccw' },
    depthStencil: {
      format: opts.depthFormat,
      depthCompare: 'less',
      depthWriteEnabled: true,
    },
  });

  const indirectBuffer = device.createBuffer({
    label: 'cars.indirect',
    size: INDIRECT_DRAW_BYTES,
    usage: GPUBufferUsage.INDIRECT | GPUBufferUsage.COPY_DST,
  });
  device.queue.writeBuffer(
    indirectBuffer, 0,
    new Uint32Array([CUBE_VERTEX_COUNT, opts.instanceCount, 0, 0]),
  );

  return {
    draw(pass) {
      if (opts.instanceCount === 0) return;
      pass.setPipeline(pipeline);
      pass.setBindGroup(1, empty.bindGroup);
      pass.setVertexBuffer(0, opts.instanceBuffer);
      pass.drawIndirect(indirectBuffer, 0);
    },
    destroy() {
      indirectBuffer.destroy();
    },
  };
}
